/**
 * The Alpha launch welcome: a one time dialog on first sign in.
 *
 * SV Cloud opened to the public wait list as an Alpha, and three facts about
 * that have to reach every new person before they build anything: things will
 * keep changing, it costs nothing right now, and the usage limits are shown
 * but not enforced, except that abuse ends an account and its apps at once.
 * The last one is the reason this is a dialog rather than a banner. A banner
 * is scrolled past; a sentence about deletion is one a person should have
 * been shown in a place they had to acknowledge.
 *
 * ORDER. It opens before the first run tour, and the tour waits for it
 * (SpotlightTour reads the same pref). Two overlays at once would put a
 * spotlight ring on a page the dialog's scrim has already dimmed.
 *
 * LANGUAGE is the first thing in it, because it is the first thing a person
 * needs: everything below it is only useful in a language they read. Picking
 * one switches the dialog in place and saves it on the account (see
 * lib/hooks/use-language.ts). The two options are named in their own
 * language, so the person looking for Thai finds the word in Thai.
 *
 * PERSISTENCE is prefs.alphaWelcomeSeen, stored beside tourDone in the same
 * localStorage key, so it is once per browser rather than once per account.
 * That is the same trade the tour already makes, and the dock's "Reset the
 * demo" re-arms both.
 */

import { useEffect, useRef } from 'react';
import { Button } from '../primitives/Button';
import { Dialog } from '../primitives/Dialog';
import { Notice } from './Notice';
import { SegmentedControl } from './SegmentedControl';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { useChooseLanguage } from '../../lib/hooks/use-language';
import { useDemoStore } from '../../lib/mock/store';
import '../../styles/components/alpha-welcome.css';

export function AlphaWelcome() {
  const seen = useDemoStore((s) => s.prefs.alphaWelcomeSeen === true);
  const setPref = useDemoStore((s) => s.setPref);
  const lang = useDemoStore((s) => s.prefs.lang);
  const chooseLanguage = useChooseLanguage();
  const viewed = useRef(false);

  useEffect(() => {
    if (seen || viewed.current) return;
    viewed.current = true;
    captureEvent('alpha_welcome_viewed', { screen_path: window.location.pathname });
  }, [seen]);

  if (seen) return null;

  function acknowledge(method: string) {
    captureEvent('alpha_welcome_acknowledged', { method });
    setPref('alphaWelcomeSeen', true);
  }

  return (
    <Dialog
      open
      onClose={() => acknowledge('dismissed')}
      title={t('Welcome to the SV Cloud Alpha')}
      description={t('SV Cloud is now open to everyone on the wait list. Here is what that means while we are in Alpha.')}
      footer={
        <Button variant="solid" data-autofocus onClick={() => acknowledge('button')}>
          {t('Got it')}
        </Button>
      }
    >
      <div className="alpha-welcome__lang">
        <p className="alpha-welcome__lang-title" id="alpha-welcome-lang">
          {t('Choose your language')}
        </p>
        <SegmentedControl<'en' | 'th'>
          label={t('Language')}
          value={lang === 'th' ? 'th' : 'en'}
          options={[
            { value: 'en', label: t('English') },
            { value: 'th', label: t('Thai') },
          ]}
          onChange={(next) => chooseLanguage(next, 'alpha_welcome')}
          describedBy="alpha-welcome-lang"
        />
        <p className="alpha-welcome__lang-note">{t('Saved to your account. You can change it later on the Account page.')}</p>
      </div>
      <ul className="alpha-welcome__list">
        <li>
          <strong>{t('This is an Alpha launch.')}</strong>{' '}
          {t('We are making updates continuously, so screens and features will change as we go.')}
        </li>
        <li>
          <strong>{t('It is free to use right now.')}</strong>{' '}
          {t('Usage is measured and shown to you, but limits are not enforced.')}
        </li>
        <li>
          <strong>{t('Tell us what you think.')}</strong>{' '}
          {t('Submit Feedback, at the bottom left, goes straight to the team.')}
        </li>
      </ul>
      <Notice tone="attention" title={t('Abuse is the exception.')}>
        {t('If we detect abuse, the account is banned immediately and its applications are deleted.')}
      </Notice>
    </Dialog>
  );
}
